import { format } from 'date-fns'

import type { ApprovalInstanceDto } from '@/Network/Types/Requests/DoctorStationExceptionRequests'
import type {
  ProgressHeaderStatus,
  ProgressLayer,
  ProgressBaseEvent,
  ProgressEvent,
  ProgressBadge
} from '@/modules/company/Approvals/AuditoriumExceptionalities/types/progress-timeline.types'
import { VALIDATION_ROLE_LABELS } from '@/types/approval-flow.types'
import { parseDecisionJustification } from '@/utils/exception-justification'

export interface ApprovalTimelineInput {
  approvalInstance: ApprovalInstanceDto
  requestDate: string | Date
  requesterName: string
  /** Texto do evento base. Default: "Pedido criado" (ou "Reversão solicitada" na reversão). */
  baseEventDescription?: string
}

export interface ApprovalTimelineProps {
  headerStatus: ProgressHeaderStatus
  title: string
  layers: ProgressLayer[]
  baseEvent: ProgressBaseEvent
}

// Mesmos rótulos da configuração do fluxo: a timeline mostrava "Operacional" onde a
// tela de configuração e o backend dizem "Operação".
const ROLE_EVENT_LABELS: Record<string, string> = VALIDATION_ROLE_LABELS

const ROLE_BADGE_MAP: Record<string, ProgressBadge> = {
  OPERATION: { text: 'Adm', variant: 'primary' },
  MEDICAL_COORDINATOR: { text: 'Med', variant: 'dark' },
  AUDIT_ANALYST: { text: 'Adm', variant: 'primary' },
  TECHNICAL_VALIDATOR: { text: 'Med', variant: 'dark' },
  STRATEGIC_VALIDATOR: { text: 'Med', variant: 'dark' },
  EXECUTIVE_VALIDATOR: { text: 'Med', variant: 'dark' }
}

function getTimezoneLabel(): string {
  const offsetMinutes = new Date().getTimezoneOffset()
  const sign = offsetMinutes <= 0 ? '+' : '-'
  const absHours = Math.floor(Math.abs(offsetMinutes) / 60)
  return `UTC${sign}${absHours}`
}

function formatTimelineDate(dateInput: string | Date): string {
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput
  if (isNaN(date.getTime())) return ''
  return format(date, 'dd/MM/yyyy')
}

function formatTimelineTime(dateInput: string | Date, userName?: string | null): string {
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput
  if (isNaN(date.getTime())) return ''
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const tz = getTimezoneLabel()
  const timeStr = `${hours}:${minutes} ${tz}`
  return userName ? `${timeStr} por ${userName}` : timeStr
}

function getDecisionDescription(role: string, decision: string): string {
  const roleLabel = ROLE_EVENT_LABELS[role] ?? role
  const statusLabel =
    decision === 'APPROVED' ? 'aprovado' : decision === 'REJECTED' ? 'recusado' : 'pendente'
  return `${roleLabel} ${statusLabel}`
}

function mapDecisionStatus(decision: string): ProgressEvent['status'] {
  if (decision === 'APPROVED') return 'approved'
  if (decision === 'REJECTED') return 'rejected'
  if (decision === 'PENDING') return 'pending'
  return 'neutral'
}

const FINISHED_HEADERS: Record<
  'COMPLETED' | 'REJECTED' | 'CANCELED',
  { type: 'approved' | 'rejected'; approval: string; reversal: string }
> = {
  COMPLETED: { type: 'approved', approval: 'Pedido aprovado', reversal: 'Reversão aprovada' },
  REJECTED: { type: 'rejected', approval: 'Pedido recusado', reversal: 'Reversão recusada' },
  CANCELED: { type: 'rejected', approval: 'Pedido cancelado', reversal: 'Reversão cancelada' }
}

function resolveHeader(
  approvalInstance: ApprovalInstanceDto,
  completedLayersCount: number
): { headerStatus: ProgressHeaderStatus; title: string } {
  const finished = FINISHED_HEADERS[approvalInstance.status as keyof typeof FINISHED_HEADERS]
  if (finished) {
    const isReversal = approvalInstance.kind === 'REVERSAL'
    return {
      headerStatus: { type: finished.type },
      title: isReversal ? finished.reversal : finished.approval
    }
  }
  const total = approvalInstance.layers.length
  return {
    headerStatus: { type: 'in-progress', current: completedLayersCount, total },
    title: `Progresso ${completedLayersCount}/${total}`
  }
}

export function mapApprovalToTimeline(input: ApprovalTimelineInput): ApprovalTimelineProps {
  const { approvalInstance, requestDate, requesterName } = input

  const sortedLayers = [...approvalInstance.layers].sort((a, b) => b.order - a.order)

  const decisionsByLayer = new Map<number, typeof approvalInstance.decisions>()
  for (const d of approvalInstance.decisions) {
    if (!decisionsByLayer.has(d.layerOrder)) decisionsByLayer.set(d.layerOrder, [])
    decisionsByLayer.get(d.layerOrder)!.push(d)
  }

  let completedLayersCount = 0
  for (const layer of approvalInstance.layers) {
    const layerDecisions = decisionsByLayer.get(layer.order) ?? []
    const allApproved =
      layerDecisions.length > 0 && layerDecisions.every((d) => d.decision === 'APPROVED')
    if (allApproved) completedLayersCount++
  }

  const isReversal = approvalInstance.kind === 'REVERSAL'
  const { headerStatus, title } = resolveHeader(approvalInstance, completedLayersCount)

  const layers: ProgressLayer[] = sortedLayers.map((layer) => {
    const operatorLabel = layer.operator === 'AND' ? 'Todos - E' : 'Qualquer um - OU'
    const layerDecisions = decisionsByLayer.get(layer.order) ?? []

    const decisionEvents: ProgressEvent[] = layerDecisions
      .sort((a, b) => {
        if (!a.decidedAt && !b.decidedAt) return 0
        if (!a.decidedAt) return -1
        if (!b.decidedAt) return 1
        return new Date(b.decidedAt).getTime() - new Date(a.decidedAt).getTime()
      })
      .map((d) => {
        const justification = parseDecisionJustification(d.justification)
        return {
          date: d.decidedAt ? formatTimelineDate(d.decidedAt) : '',
          description: getDecisionDescription(d.role, d.decision),
          time: d.decidedAt ? formatTimelineTime(d.decidedAt, d.userName) : '',
          status: mapDecisionStatus(d.decision),
          badge: ROLE_BADGE_MAP[d.role],
          ...(justification.length > 0 && { justification })
        }
      })

    const allApproved =
      layerDecisions.length > 0 && layerDecisions.every((d) => d.decision === 'APPROVED')

    const events: ProgressEvent[] = []

    if (allApproved && layerDecisions.length >= 2) {
      const latestDecision = layerDecisions.reduce((latest, d) => {
        if (!d.decidedAt) return latest
        if (!latest.decidedAt) return d
        return new Date(d.decidedAt) > new Date(latest.decidedAt) ? d : latest
      })

      events.push({
        date: latestDecision.decidedAt ? formatTimelineDate(latestDecision.decidedAt) : '',
        description: 'Etapa concluída',
        time: latestDecision.decidedAt
          ? formatTimelineTime(latestDecision.decidedAt, latestDecision.userName)
          : '',
        status: 'approved'
      })
    }

    events.push(...decisionEvents)

    return { operatorLabel, events }
  })

  const baseEvent: ProgressBaseEvent = {
    date: formatTimelineDate(requestDate),
    description:
      input.baseEventDescription ?? (isReversal ? 'Reversão solicitada' : 'Pedido criado'),
    time: formatTimelineTime(requestDate, requesterName)
  }

  return { headerStatus, title, layers, baseEvent }
}

/**
 * Quem iniciou a reversão e quando: o primeiro voto decidido da instância de
 * reversão — o clique de reverter já conta como voto. O DTO não traz a data de
 * criação da instância, então a decisão mais antiga é a referência.
 */
export function getReversalRequestEvent(
  reversalInstance: ApprovalInstanceDto
): { requestDate: string; requesterName: string; justification: string | null } | null {
  const decided = reversalInstance.decisions
    .filter((d) => d.decidedAt && d.decision !== 'PENDING')
    .sort((a, b) => new Date(a.decidedAt!).getTime() - new Date(b.decidedAt!).getTime())
  const first = decided[0]
  if (!first) return null
  return {
    requestDate: first.decidedAt!,
    requesterName: first.userName ?? '',
    justification: first.justification
  }
}

/** Timeline da instância de reversão, com o evento base "Reversão solicitada". */
export function mapReversalToTimeline(
  reversalInstance: ApprovalInstanceDto
): ApprovalTimelineProps {
  const request = getReversalRequestEvent(reversalInstance)
  return mapApprovalToTimeline({
    approvalInstance: { ...reversalInstance, kind: 'REVERSAL' },
    requestDate: request?.requestDate ?? '',
    requesterName: request?.requesterName ?? '',
    baseEventDescription: 'Reversão solicitada'
  })
}
